import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  createClasses,
  OutlinedButton,
  ToastError,
} from "@bloomberg/media-ui-core";
import type { DropdownOption } from "@bloomberg/media-ui-dropdown/types";
import { ButtonDropdown } from "@bloomberg/media-ui-dropdown";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useSessionStorage } from "@foundation/local-storage";
import { ChevronLeft, ChevronRight } from "@bloomberg/media-ui-icons";
import styles from "./styles.module.css";
import WatchlistSecurityTypeGrouping from "~components/WatchlistSecurityTypeGrouping";
import WatchlistHeatmap from "~components/WatchlistHeatmap";
import { DashboardViewType, type MarketValue } from "~types/portfolio";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import SummaryOverview from "~components/WatchlistSummaryOverview";
import WatchlistFlexiCompare from "~components/WatchlistFlexiCompare";
import { updateUserPreferences } from "~api/portfolio/portfolioData";
import { DASHBOARD_v2_FEEDBACK_URL } from "~constants/Portfolio";
import ShareFeedback from "~components/ShareFeedback";
import { savePortfolio } from "~utils/portfolio/createPortfolio";
import { THREE_SECONDS, FOUR_SECONDS } from "~constants";

const DASHBOARD_VIEW_OPTIONS = [
  {
    displayValue: "Overview",
    value: "DASH",
  },
  {
    displayValue: "Comparison Chart",
    value: "COMP",
  },
  {
    displayValue: "Heatmap",
    value: "HTMP",
  },
  {
    displayValue: "Collapsed View",
    value: "HIDE",
  },
];

type WatchlistDashboardV2Props = {
  marketValue: MarketValue;
};

type DotProps = {
  itemId: DashboardViewType;
  onClick: (value: DashboardViewType) => void;
  isActive: boolean;
};

function Dot({ onClick, itemId, isActive }: DotProps) {
  const handleOnClick = useCallback(() => {
    onClick(itemId);
  }, [onClick, itemId]);

  return (
    <span
      className={createClasses([styles.dot, isActive && styles.activeDot])}
      aria-label="navigation dot"
      role="button"
      tab-index={0}
      onClick={handleOnClick}
    />
  );
}

export default function WatchlistDashboardV2(props: WatchlistDashboardV2Props) {
  const { marketValue } = props;

  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const { dashboardView, updateDashboardView, upsertPortfolio } =
    usePortfolioPage();

  const [selectedView, setSelectedView] = useState<DashboardViewType>("DASH");
  const [carouselView, setCarouselView] = useState<DashboardViewType>("DASH");
  const [shouldShowError, setShouldShowError] = useState(false);
  const showError = useCallback(() => setShouldShowError(true), []);
  const hideError = useCallback(() => setShouldShowError(false), []);
  const [bundleIntent, setBundleIntent] = useSessionStorage(
    "createBundleAfterLogin",
    null
  );

  // refresh UI on dashboard view change
  useEffect(() => {
    setSelectedView(dashboardView);
    setCarouselView(dashboardView);
  }, [dashboardView]);

  // Create bundle after login
  useEffect(() => {
    if (bundleIntent && bundleIntent.shouldCreate) {
      savePortfolio(bundleIntent.bundlePortfolio, bconnectId)
        .then(upsertPortfolio)
        .catch(() => {
          showError();
          setTimeout(() => hideError(), FOUR_SECONDS);
        });
      setBundleIntent(null);
    }
  }, [
    bundleIntent,
    setBundleIntent,
    bconnectId,
    upsertPortfolio,
    hideError,
    showError,
  ]);

  const trackDashboardViewChanges = (value: DashboardViewType) => {
    const event = value === "HIDE" ? "watchlistHide" : "watchlistShow";
    let eventLabel;

    switch (value) {
      case "DASH":
        eventLabel = "show_charts";
        break;
      case "COMP":
        eventLabel = "show_comparison_chart";
        break;
      case "HTMP":
        eventLabel = "show_heatmap";
        break;
      case "HIDE":
        eventLabel = "hide_charts";
        break;
      default:
        eventLabel = "show_charts";
    }

    trackClickEvent(event, eventLabel, "click", "watchlist");
  };

  const updateCarouselView = (value: DashboardViewType) => {
    // update state
    setCarouselView(value);

    // track GA event
    trackDashboardViewChanges(value);
  };

  const onSelect = useCallback(
    (option?: DropdownOption | null) => {
      if (!option?.value) return;

      // update dashboard view user preference, only on dropdown action change
      updateUserPreferences({
        bconnectId,
        dashboardView: option.value,
      });

      updateDashboardView(option.value as DashboardViewType);

      // track GA event
      trackDashboardViewChanges(option.value as DashboardViewType);
    },
    [bconnectId, updateDashboardView]
  );

  const nextCarousel = () => {
    let nextView: DashboardViewType = carouselView;

    switch (carouselView) {
      case "DASH":
      case "COMP":
        nextView = "HTMP";
        break;
      case "HTMP":
        nextView = "STYP";
        break;
    }

    // update state and track event
    updateCarouselView(nextView);
  };

  const prevCarousel = () => {
    let nextView: DashboardViewType = carouselView;

    switch (carouselView) {
      case "HTMP":
        nextView = "COMP";
        break;
      case "STYP":
        nextView = "HTMP";
        break;
    }

    // update state and track event
    updateCarouselView(nextView);
  };

  const selectedItem = useMemo(
    () =>
      DASHBOARD_VIEW_OPTIONS.find((option) => option.value === selectedView),
    [selectedView]
  );

  const getDashboardViewClass = () => {
    switch (carouselView) {
      case "DASH":
        return styles.viewDashboard;
      case "COMP":
        return styles.viewFlexiCompare;
      case "HTMP":
        return styles.viewHeatmap;
      case "STYP":
        return styles.viewSecurityType;
      case "HIDE":
        return styles.hideDashboard;
      default:
        return styles.viewDashboard;
    }
  };

  return (
    <div className={styles.watchlistDashboardV2}>
      <div className={styles.overview}>
        <SummaryOverview marketValue={marketValue}></SummaryOverview>
        <div className={styles.dashboardViewDropdownContainer}>
          <ButtonDropdown
            className={styles.dashboardViewDropdown}
            selectedItemOverride={selectedItem}
            onSelect={onSelect}
            options={DASHBOARD_VIEW_OPTIONS}
          />
        </div>
      </div>
      <div className={styles.horizontalLine} role="separator" />
      <div
        className={createClasses([
          styles.dashboardContainer,
          getDashboardViewClass(),
        ])}
      >
        <div className={styles.flexiCompare}>
          <WatchlistFlexiCompare />
        </div>
        <div
          className={createClasses([styles.separator, styles.horizontal])}
          role="separator"
        />
        <div className={styles.heatmap}>
          <WatchlistHeatmap />
        </div>
        <div className={styles.separator} role="separator" />
        <div className={styles.symbolType}>
          <WatchlistSecurityTypeGrouping version="v2" />
        </div>
      </div>
      <div
        className={createClasses([
          styles.carouselNavigator,
          { [styles.hideDashboard]: dashboardView !== "DASH" },
        ])}
      >
        <div className={styles.navigator}>
          <Dot
            itemId="DASH"
            isActive={carouselView === "DASH" || carouselView === "COMP"}
            onClick={updateCarouselView}
          />
          <Dot
            itemId="HTMP"
            isActive={carouselView === "HTMP"}
            onClick={updateCarouselView}
          />
          <Dot
            itemId="STYP"
            isActive={carouselView === "STYP"}
            onClick={updateCarouselView}
          />
        </div>
        <div className={styles.navigatorButtonWrapper}>
          <OutlinedButton
            className={styles.navigatorIconButton}
            disabled={carouselView === "DASH" || carouselView === "COMP"}
            onClick={prevCarousel}
            version="phoenix"
          >
            <ChevronLeft />
          </OutlinedButton>
          <OutlinedButton
            className={styles.navigatorIconButton}
            disabled={carouselView === "STYP"}
            onClick={nextCarousel}
            version="phoenix"
          >
            <ChevronRight />
          </OutlinedButton>
        </div>
      </div>
      <div
        className={createClasses([
          styles.feedbackSection,
          { [styles.hideDashboard]: dashboardView === "HIDE" },
        ])}
      >
        <span className={styles.feedbackWrapper}>
          What would make this experience better for you?{" "}
          <ShareFeedback
            className={styles.feedbackText}
            feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
            feedbackText="Share your thoughts"
            trackingEventLabel="survey_banner_watchlist_dashboard"
          />
        </span>
      </div>
      {shouldShowError && <ToastError open autoHideDuration={THREE_SECONDS} />}
    </div>
  );
}
